// Utility functions for CoandaView

const Utils = {
  // Format numbers with proper units
  formatNumber(value, decimals = 2) {
    if (value === null || value === undefined) return 'N/A';
    if (Math.abs(value) < 0.01) return value.toExponential(decimals);
    return value.toFixed(decimals);
  },

  // Format memory sizes
  formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Format time
  formatTime(seconds) {
    if (seconds < 60) return seconds.toFixed(2) + 's';
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${minutes}m ${secs}s`;
  },

  // Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  // Throttle function
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Generate unique ID
  generateId() {
    return 'id-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  },

  // Deep clone object
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  },

  // Clamp value between min and max
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  },

  // Linear interpolation
  lerp(a, b, t) {
    return a + (b - a) * t;
  },

  // Map value from one range to another
  map(value, inMin, inMax, outMin, outMax) {
    return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
  },

  // Download data as file
  downloadFile(data, filename, type = 'application/json') {
    if (data instanceof Blob) {
      this.downloadBlob(data, filename);
      return;
    }

    const blob = data instanceof ArrayBuffer
      ? new Blob([data], { type })
      : new Blob([data], { type });

    this.downloadBlob(blob, filename);
  },

  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  async downloadDataURL(dataURL, filename) {
    try {
      const response = await fetch(dataURL);
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      try {
        const parts = dataURL.split(',');
        if (parts.length < 2) throw error;
        const header = parts[0];
        const mimeMatch = header.match(/data:(.*);base64/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        const byteString = atob(parts[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
          uintArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([uintArray], { type: mimeType });
        this.downloadBlob(blob, filename);
      } catch (fallbackError) {
        console.error('Failed to download data URL:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // Read file as text
  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  },

  // Read file as array buffer
  readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  },

  // Show notification
  notify(message, type = 'info') {
    console.log(`[${type.toUpperCase()}] ${message}`);

    // Create toast container if it doesn't exist
    let container = document.getElementById('toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'toast-container';
      container.style.cssText = `
        position: fixed;
        top: 80px;
        right: 20px;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 10px;
        pointer-events: none;
      `;
      document.body.appendChild(container);
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    // Color scheme based on type
    const colors = {
      info: { bg: '#3b82f6', border: '#2563eb' },
      success: { bg: '#22c55e', border: '#16a34a' },
      warning: { bg: '#f59e0b', border: '#d97706' },
      error: { bg: '#ef4444', border: '#dc2626' }
    };
    const color = colors[type] || colors.info;

    toast.style.cssText = `
      background: ${color.bg};
      border: 1px solid ${color.border};
      color: white;
      padding: 12px 40px 12px 16px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      font-size: 14px;
      max-width: 400px;
      pointer-events: auto;
      opacity: 0;
      transform: translateX(100%);
      transition: opacity 0.3s, transform 0.3s;
      position: relative;
      cursor: grab;
      user-select: none;
    `;

    // Create message span
    const messageSpan = document.createElement('span');
    messageSpan.textContent = message;
    toast.appendChild(messageSpan);

    // Create close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(255,255,255,0.8);
      font-size: 20px;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;
      transition: color 0.2s;
    `;
    closeBtn.onmouseenter = () => closeBtn.style.color = 'white';
    closeBtn.onmouseleave = () => closeBtn.style.color = 'rgba(255,255,255,0.8)';
    closeBtn.onclick = (e) => {
      e.stopPropagation();
      dismissToast();
    };
    toast.appendChild(closeBtn);

    container.appendChild(toast);

    // Dismiss function
    let timeoutId;
    const dismissToast = () => {
      clearTimeout(timeoutId);
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => toast.remove(), 300);
    };

    // Swipe to dismiss
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    toast.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
      toast.style.cursor = 'grabbing';
      toast.style.transition = 'opacity 0.3s';
    });

    toast.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      currentX = e.clientX - startX;
      if (currentX > 0) {
        toast.style.transform = `translateX(${currentX}px)`;
        toast.style.opacity = Math.max(0, 1 - currentX / 150);
      }
    });

    toast.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      toast.style.cursor = 'grab';
      toast.style.transition = 'opacity 0.3s, transform 0.3s';
      if (currentX > 80) {
        dismissToast();
      } else {
        toast.style.transform = 'translateX(0)';
        toast.style.opacity = '1';
      }
      currentX = 0;
    });

    toast.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        toast.style.cursor = 'grab';
        toast.style.transition = 'opacity 0.3s, transform 0.3s';
        if (currentX > 80) {
          dismissToast();
        } else {
          toast.style.transform = 'translateX(0)';
          toast.style.opacity = '1';
        }
        currentX = 0;
      }
    });

    // Touch support for mobile
    toast.addEventListener('touchstart', (e) => {
      isDragging = true;
      startX = e.touches[0].clientX;
      toast.style.transition = 'opacity 0.3s';
    });

    toast.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX - startX;
      if (currentX > 0) {
        toast.style.transform = `translateX(${currentX}px)`;
        toast.style.opacity = Math.max(0, 1 - currentX / 150);
      }
    });

    toast.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      toast.style.transition = 'opacity 0.3s, transform 0.3s';
      if (currentX > 80) {
        dismissToast();
      } else {
        toast.style.transform = 'translateX(0)';
        toast.style.opacity = '1';
      }
      currentX = 0;
    });

    // Animate in
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(0)';
    });

    // Auto-remove after delay
    const duration = type === 'error' ? 5000 : 3000;
    timeoutId = setTimeout(() => {
      dismissToast();
    }, duration);
  },

  // Parse URL parameters
  getUrlParams() {
    const params = {};
    const queryString = window.location.search.substring(1);
    const pairs = queryString.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key) {
        params[decodeURIComponent(key)] = decodeURIComponent(value || '');
      }
    }
    return params;
  },

  // Set URL parameter without reload
  setUrlParam(key, value) {
    const url = new URL(window.location);
    url.searchParams.set(key, value);
    window.history.pushState({}, '', url);
  },

  // Calculate bounding box
  calculateBoundingBox(points) {
    if (!points || points.length === 0) {
      return { min: [0, 0, 0], max: [0, 0, 0] };
    }

    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];

    for (const point of points) {
      for (let i = 0; i < 3; i++) {
        min[i] = Math.min(min[i], point[i]);
        max[i] = Math.max(max[i], point[i]);
      }
    }

    return { min, max };
  },

  // Calculate distance between two points
  distance(p1, p2) {
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const dz = p2[2] - p1[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  },

  // Normalize vector
  normalize(v) {
    const length = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    if (length === 0) return [0, 0, 0];
    return [v[0] / length, v[1] / length, v[2] / length];
  },

  // Cross product
  cross(a, b) {
    return [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0]
    ];
  },

  // Dot product
  dot(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  },

  /**
   * Download a folder with progress indicator animation
   * @param {string} path - The folder path to download
   * @param {string} name - Display name for the download
   * @param {HTMLElement} buttonElement - Button element with progress indicator
   * @param {number} perimeter - SVG rect perimeter for stroke animation (default 124 for 36x36 btn-icon, 95.4 for 28x28 btn-action)
   */
  async downloadFolderWithProgress(path, name, buttonElement = null, perimeter = 124) {
    const displayName = name || path.split('/').pop();

    // Check if this is a single file (geometry file) or folder
    // Check both path and name (name is useful when path is a UUID)
    const lowerPath = path.toLowerCase();
    const lowerName = (name || '').toLowerCase();
    const singleFileExtensions = ['.stl', '.obj', '.vtk', '.vtu', '.vtp', '.ply', '.case', '.cas', '.step', '.stp', '.iges', '.igs'];
    const isSingleFile = singleFileExtensions.some(ext => lowerPath.endsWith(ext) || lowerName.endsWith(ext));

    // Helper to update square border progress
    const updateProgress = (percent) => {
      if (!buttonElement) return;
      const progressRing = buttonElement.querySelector('.progress-ring');
      const progressText = buttonElement.querySelector('.progress-text');
      if (progressRing) {
        const offset = perimeter - (percent / 100) * perimeter;
        progressRing.style.strokeDashoffset = offset;
      }
      if (progressText) {
        progressText.textContent = `${Math.round(percent)}%`;
      }
    };

    // Helper to reset button state
    const resetButton = () => {
      if (!buttonElement) return;
      buttonElement.classList.remove('downloading');
      updateProgress(0);
    };

    try {
      // Start download - show progress indicator
      if (buttonElement) {
        buttonElement.classList.add('downloading');
        updateProgress(0);
      }

      // Get auth token
      const token = window.Auth?.getToken?.() || localStorage.getItem('coandacloud_token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      if (isSingleFile) {
        // Single file download
        const response = await fetch(`/api/download-file?path=${encodeURIComponent(path)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || `Download failed: ${response.status}`);
        }

        const blob = await response.blob();
        updateProgress(100);
        this.downloadBlob(blob, displayName);
        this.notify(`Downloaded "${displayName}"`, 'success');
      } else {
        // Folder download with progress tracking
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/download-folder', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.responseType = 'blob';

          xhr.onprogress = (event) => {
            if (event.lengthComputable) {
              const percent = (event.loaded / event.total) * 100;
              updateProgress(percent);
            } else {
              // If size unknown, show indeterminate progress up to 90%
              const currentText = buttonElement?.querySelector('.progress-text')?.textContent || '0%';
              const currentPercent = parseInt(currentText) || 0;
              if (currentPercent < 90) {
                updateProgress(currentPercent + 5);
              }
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              updateProgress(100);
              resolve(xhr.response);
            } else {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const error = JSON.parse(reader.result);
                  reject(new Error(error.detail || `Download failed: ${xhr.status}`));
                } catch {
                  reject(new Error(`Download failed: ${xhr.status}`));
                }
              };
              reader.onerror = () => reject(new Error(`Download failed: ${xhr.status}`));
              reader.readAsText(xhr.response);
            }
          };

          xhr.onerror = () => reject(new Error('Network error during download'));
          xhr.ontimeout = () => reject(new Error('Download timed out'));

          xhr.send(JSON.stringify({ folder_path: path }));
        });

        this.downloadBlob(blob, `${displayName}.zip`);
        this.notify(`Downloaded "${displayName}.zip"`, 'success');
      }

      // Reset button after short delay to show 100%
      setTimeout(resetButton, 500);
    } catch (e) {
      console.error('Download error:', e);
      this.notify(`Download failed: ${e.message}`, 'error');
      resetButton();
    }
  }
};

// Export for use in other modules
window.Utils = Utils;
